import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, Check } from "lucide-react";
import { useLocale } from "@/hooks/use-locale";
import { ACTIVE_LANGS } from "@/hooks/use-locale";
import { SUPPORTED_LANGS, LANG_LABELS, type SupportedLang } from "@/lib/translations.functions";

/**
 * The list is rendered in a portal with fixed positioning so it can never be
 * clipped by a scrolling/overflow-hidden parent (mobile menu, sticky header).
 */
export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const [lang, setLang] = useLocale();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 16;
    const above = r.top - 16;
    const openUp = below < 200 && above > below;
    const width = 176; // w-44
    // Anchor to the button, then clamp inside the viewport so the list can
    // never run off either edge on small screens.
    const left = Math.min(Math.max(8, r.right - width), Math.max(8, window.innerWidth - width - 8));
    setPos({
      top: openUp ? Math.max(8, r.top - 8 - Math.min(above, 320)) : r.bottom + 8,
      left,
      maxHeight: Math.max(160, Math.min(320, openUp ? above : below)),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" data-no-translate>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition hover:border-primary"
      >
        <Globe className="h-3.5 w-3.5" />
        {compact ? lang.toUpperCase() : LANG_LABELS[lang]}
      </button>

      {open &&
        mounted &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            role="menu"
            aria-label="Languages"
            data-no-translate
            style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight }}
            className="fixed z-[200] w-44 overflow-y-auto overscroll-contain rounded-xl border border-border bg-card shadow-2xl"
          >
            {ACTIVE_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                role="menuitemradio"
                aria-checked={l === lang}
                onClick={() => {
                  setLang(l as SupportedLang);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition hover:bg-accent ${
                  l === lang ? "text-primary" : "text-foreground"
                }`}
              >
                <span>{LANG_LABELS[l as SupportedLang]}</span>
                {l === lang && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
